import { useMemo } from "react";
import { useSearchParams } from "react-router";
import { Alert, Table } from "antd";
import type { ColumnsType } from "antd/es/table/interface";

import PageHeader from "@/components/UI/PageHeader";
import { SpirometryDrawer } from "@/components/Waveforms";
import { useSpirometryEffortsQuery } from "@/hooks/useWaveformQueries";

type SpirometryEffortRow = {
  transmittal_number: number;
  effort_number?: number | null;
  subject_id?: string | null;
  participant_id?: string | null;
  sponsor_site_id?: string | null;
  protocol_id?: string | null;
  collection_date?: string | null;
  effort_time?: string | null;
  n_samples?: number | null;
};

const columns: ColumnsType<SpirometryEffortRow> = [
  {
    title: "Subject",
    dataIndex: "subject_id",
    sorter: (a, b) => (a.subject_id ?? "").localeCompare(b.subject_id ?? ""),
  },
  {
    title: "Collection Date",
    dataIndex: "collection_date",
    sorter: (a, b) =>
      (a.collection_date ?? "").localeCompare(b.collection_date ?? ""),
  },
  {
    title: "Effort Time",
    dataIndex: "effort_time",
    sorter: (a, b) => (a.effort_time ?? "").localeCompare(b.effort_time ?? ""),
  },
  {
    title: "Transmittal Number",
    dataIndex: "transmittal_number",
    align: "right",
    sorter: (a, b) => a.transmittal_number - b.transmittal_number,
  },
  {
    title: "Effort Number",
    dataIndex: "effort_number",
    align: "right",
    sorter: (a, b) => (a.effort_number ?? 0) - (b.effort_number ?? 0),
  },
  {
    title: "Site",
    dataIndex: "sponsor_site_id",
    sorter: (a, b) =>
      (a.sponsor_site_id ?? "").localeCompare(b.sponsor_site_id ?? ""),
  },
  {
    title: "Samples",
    dataIndex: "n_samples",
    align: "right",
    sorter: (a, b) => (a.n_samples ?? 0) - (b.n_samples ?? 0),
    render: (nSamples: number | null | undefined) =>
      nSamples != null ? nSamples.toLocaleString("en-US") : "—",
  },
];

export default function Spirometry() {
  const [searchParams, setSearchParams] = useSearchParams();
  const transmittalParam = searchParams.get("transmittal");
  const parsedTransmittal = transmittalParam ? Number(transmittalParam) : null;
  const openTransmittal =
    parsedTransmittal !== null && Number.isFinite(parsedTransmittal)
      ? parsedTransmittal
      : null;

  const { data, isLoading, error } = useSpirometryEffortsQuery();
  const rows: SpirometryEffortRow[] = useMemo(
    () => data?.efforts ?? [],
    [data],
  );

  const setTransmittalParam = (transmittalNumber: number | null) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (transmittalNumber !== null) {
          next.set("transmittal", String(transmittalNumber));
        } else {
          next.delete("transmittal");
        }
        return next;
      },
      { replace: true },
    );
  };

  const openEffort = useMemo(
    () =>
      rows.find((row) => row.transmittal_number === openTransmittal) ?? null,
    [rows, openTransmittal],
  );

  return (
    <div className="w-full p-4">
      <div className="flex justify-between mb-4 relative z-20">
        <div className="flex gap-2">
          <PageHeader
            title="Measures: Spirometry"
            tooltipText="Spirometry efforts per subject per visit. Click a row to open the flow-volume waveform viewer."
          />
        </div>
      </div>
      {error ? (
        <Alert
          type="error"
          showIcon
          message="Failed to load spirometry efforts"
          description="Something went wrong while fetching spirometry data. Please try again later."
        />
      ) : (
        <Table<SpirometryEffortRow>
          dataSource={rows}
          columns={columns}
          loading={isLoading}
          rowKey={(record) =>
            `${record.transmittal_number}-${record.effort_number ?? ""}`
          }
          size="small"
          onRow={(record) => ({
            style: { cursor: "pointer" },
            onClick: () => setTransmittalParam(record.transmittal_number),
          })}
        />
      )}
      <SpirometryDrawer
        open={openTransmittal !== null}
        onClose={() => setTransmittalParam(null)}
        transmittalNumber={openTransmittal}
        effortMeta={
          openEffort
            ? {
                subject_id: openEffort.subject_id,
                collection_date: openEffort.collection_date,
              }
            : undefined
        }
      />
    </div>
  );
}
